import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { GlobalSettings, TlsCertRead, TlsCertWrite } from 'common/api/v1/types'
import { Role } from 'common/api/v1/types'
import store, { Api } from '../store'
import { keys } from './keys'
import { enqueueSuccessSnackbar } from '../redux/actions/notificationActions'
import { useUser } from '../utils'

export type SettingsData = { settings: GlobalSettings; tls: TlsCertRead }

export const useSettings = () => {
  const user = useUser()
  return useQuery({
    queryKey: keys.settings.all,
    queryFn: async (): Promise<SettingsData> => {
      const [settings, tls] = await Promise.all([Api.settingsApi.getSettings(), Api.tlsApi.getTls()])
      return { settings, tls }
    },
    enabled: user.role !== Role.basic,
    meta: { operation: 'fetch settings' },
  })
}

const SAVE_SETTINGS_KEY = ['settings', 'save'] as const
export const useSaveSettings = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: SAVE_SETTINGS_KEY,
    mutationFn: (settings: GlobalSettings) => Api.settingsApi.saveSettings(settings),
    onSuccess: () => {
      store.dispatch(enqueueSuccessSnackbar('Global settings updated'))
      void queryClient.invalidateQueries({ queryKey: keys.settings.all })
    },
    meta: { operation: 'update global settings' },
  })
}
export const useIsSavingSettings = () => useIsMutating({ mutationKey: SAVE_SETTINGS_KEY }) > 0

const SAVE_TLS_KEY = ['settings', 'saveTls'] as const
export const useSaveTls = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: SAVE_TLS_KEY,
    mutationFn: (tls: TlsCertWrite) => Api.tlsApi.saveTls(tls),
    onSuccess: () => {
      store.dispatch(enqueueSuccessSnackbar('TLS certificate saved successfully'))
      void queryClient.invalidateQueries({ queryKey: keys.settings.all })
    },
    meta: { operation: 'update tls settings' },
  })
}
export const useIsSavingTls = () => useIsMutating({ mutationKey: SAVE_TLS_KEY }) > 0
